import type { EventBus } from '../core/events'
import { RNG } from '../core/rng'
import { LINE_BANK, lineText, lineWeight, type BankLine, type DaiseEventKey } from './bank'
import type { DaiseLineProvider } from './liveClient'
import { BankLineProvider } from './liveClient'

export interface DaiseMessage {
  text: string
  /** urgency shifts presentation color in the feed */
  tone: 'log' | 'alert' | 'feature'
  /** feed channel: absent/'net' is the host; 'sig' is a signal intrusion (SIG prefix, own accent) */
  channel?: 'net' | 'sig'
}

/**
 * DAISE — the host. Subscribes to game events, selects a line (weighted
 * random, no immediate repeats), and dispatches it to the on-screen system
 * feed. v1 is bank-only; the live provider hook exists but is parked (§10).
 */
export class DaiseHost {
  private lastLine = new Map<DaiseEventKey, string>()
  private cooldownT = 0
  private queue: DaiseMessage[] = []
  private rng = new RNG(0xdaa15e)
  private unsubs: (() => void)[] = []
  private provider: DaiseLineProvider = new BankLineProvider()
  // run context for conditioned line pools (see variant()/conditioned())
  private mode: 'campaign' | 'endless' = 'campaign'
  private act = 1

  constructor(
    bus: EventBus,
    private present: (msg: DaiseMessage) => void,
    private lineCooldown: number,
    provider?: DaiseLineProvider
  ) {
    if (provider) this.provider = provider
    const say = (key: DaiseEventKey, tone: DaiseMessage['tone'] = 'log', force = false) => {
      this.enqueue(key, tone, force)
    }
    this.unsubs.push(
      bus.on('run_start', ({ mode }) => {
        this.mode = mode
        this.act = 1
        say('run_start', 'feature', true)
      }),
      bus.on('player_starving', () => say(this.conditioned('starving'), 'alert')),
      bus.on('player_death_cheap', () => say('death_cheap', 'feature', true)),
      bus.on('player_death', () => say(this.conditioned('death'), 'feature', true)),
      bus.on('high_score', () => say('high_score', 'feature')),
      bus.on('requisition_purchase', () => say('requisition', 'log', true)),
      bus.on('floor_transition', ({ act }) => {
        this.act = act
        say(this.conditioned('floor_transition'), 'log', true)
      }),
      bus.on('boss_intro', ({ bossId }) => say(this.variant('boss_intro', bossId), 'feature', true)),
      bus.on('boss_phase', () => say('boss_phase', 'alert')),
      bus.on('boss_kill', () => say('boss_kill', 'feature', true)),
      bus.on('achievement', ({ title }) => this.enqueue('achievement', 'log', false, ` Entry: ${title}.`)),
      bus.on('magic_used', () => say('magic_used')),
      bus.on('death_type_spawn', () => say('death_type_spawn', 'alert', true)),
      bus.on('death_type_removed', () => say('death_type_removed')),
      bus.on('generator_destroyed', () => say(this.conditioned('generator_destroyed'))),
      bus.on('level_clear', () => say(this.conditioned('level_clear'), 'log', true)),
      bus.on('run_end', () => say('run_end', 'feature', true)),
      bus.on('campaign_won', () => say('campaign_won', 'feature', true)),
      bus.on('treasure', () => this.rng.chance(0.25) && say('treasure')),
      bus.on('secret_found', () => say('secret_found')),
      bus.on('teleport', () => this.rng.chance(0.3) && say('teleport')),
      bus.on('amenity_purchase', () => say('amenity_purchase')),
      bus.on('residual_unlock', () => say('residual_unlock', 'feature', true)),
      bus.on('override_engaged', () => say('override_engaged', 'feature', true)),
      bus.on('armament_pickup', () => say('armament_pickup')),
      bus.on('armament_exhausted', () => this.rng.chance(0.6) && say('armament_exhausted')),
      bus.on('relay_latched', ({ remaining }) => remaining > 0 && this.rng.chance(0.5) && say('relay_latched')),
      bus.on('shutters_open', () => say('shutters_open')),
      bus.on('endless_unlocked', () => say('endless_unlocked', 'feature', true)),
      bus.on('endless_milestone', () => say('endless_milestone', 'feature', true)),
      bus.on('key_pickup', () => say('key_pickup')),
      bus.on('door_unlocked', () => say('door_unlocked')),
      // traps re-trigger on a short cooldown — gate so repeated hits don't spam the feed
      bus.on('trap_hit', () => this.rng.chance(0.35) && say('trap_hit')),
      bus.on('life_lost', () => say('life_lost', 'alert', true))
    )
  }

  /** advance cooldown + flush queue; call once per frame with real dt */
  update(dt: number): void {
    this.cooldownT -= dt
    if (this.cooldownT <= 0 && this.queue.length > 0) {
      const msg = this.queue.shift()!
      this.present(msg)
      this.cooldownT = this.lineCooldown
    }
  }

  /**
   * Scripted line injection for moments the event wiring can't know about
   * (first-run orientation, archive recoveries). Priority path: jumps queued
   * chatter. `suffix` appends detail (e.g. a recovered record's title).
   */
  announce(key: DaiseEventKey, tone: DaiseMessage['tone'] = 'alert', suffix = ''): void {
    this.enqueue(key, tone, true, suffix)
  }

  /**
   * Exact scripted text on the priority path — used by the ending vignettes,
   * whose lines are ordered, not drawn from a pool. Unlike forced bank lines
   * this never culls the queue: scripted lines must all present, whatever
   * their tone (the quiet ending's register-drop line is tone 'log').
   * Callers wanting a clean slate call clearQueue() first.
   */
  announceText(text: string, tone: DaiseMessage['tone'] = 'feature'): void {
    this.queue.push({ text, tone })
    this.cooldownT = Math.min(this.cooldownT, 0.4)
  }

  /** drop all pending chatter — the ending vignette starts from silence */
  clearQueue(): void {
    this.queue = []
  }

  /**
   * A signal intrusion: a voice that is not the host cuts into the feed.
   * Priority path (same pacing as forced lines), rendered on the 'sig'
   * channel — the feed swaps the NET prefix and accent for the intrusion's.
   * Line selection/gating (one per floor, RNG stream) is the game's concern.
   */
  intrude(text: string): void {
    this.queue.push({ text, tone: 'alert', channel: 'sig' })
    this.cooldownT = Math.min(this.cooldownT, 0.4)
  }

  /**
   * Run-context sync for floors loaded without a floor_transition emit
   * (run start, dev ?floor= jumps) — conditioned pools must match the floor
   * actually on air, not the act the previous run ended on.
   */
  setAct(act: number): void {
    this.act = act
  }

  /** `base_suffix` if that pool exists in the bank, else `base` (boss_intro_warden → boss_intro). */
  private variant(base: DaiseEventKey, suffix: string): DaiseEventKey {
    const k = `${base}_${suffix}`
    return k in LINE_BANK ? (k as DaiseEventKey) : base
  }

  /**
   * Depth/mode conditioning, chained: endless → act3 → act2 → base. Each tier
   * only claims the pick when its pool exists, so sparse variant coverage
   * degrades to the nearest register instead of the flat base voice.
   * DECISION: overtime inside a won campaign run (act > 3 — procgen acts keep
   * counting past the schedule) also draws the endless pools; the cyclical
   * lines are about being past the schedule, not about which menu started
   * the run.
   */
  private conditioned(base: DaiseEventKey): DaiseEventKey {
    if (this.mode === 'endless' || this.act > 3) {
      const k = this.variant(base, 'endless')
      if (k !== base) return k
    }
    if (this.act >= 3) {
      const k = this.variant(base, 'act3')
      if (k !== base) return k
    }
    if (this.act >= 2) return this.variant(base, 'act2')
    return base
  }

  /** weighted pick over a pool (bare strings weigh 1; see BankLine) */
  private pickWeighted(pool: readonly BankLine[]): string {
    let total = 0
    for (const l of pool) total += lineWeight(l)
    let r = this.rng.next() * total
    for (const l of pool) {
      r -= lineWeight(l)
      if (r < 0) return lineText(l)
    }
    return lineText(pool[pool.length - 1]!)
  }

  /** `suffix` appends detail (e.g. an achievement title) to the bank line — one feed entry */
  private enqueue(key: DaiseEventKey, tone: DaiseMessage['tone'], force = false, suffix = ''): void {
    const lines = LINE_BANK[key]
    if (!lines || lines.length === 0) return
    // avoid immediate repeats per event type
    const last = this.lastLine.get(key)
    const pool = lines.length > 1 ? lines.filter((l) => lineText(l) !== last) : lines
    const picked = this.provider.line(key, this.pickWeighted(pool))
    this.lastLine.set(key, picked)
    const text = picked + suffix
    if (force) {
      // priority lines jump the queue: drop pending log chatter, keep alerts/features
      this.queue = this.queue.filter((m) => m.tone !== 'log')
      this.queue.push({ text, tone })
      this.cooldownT = Math.min(this.cooldownT, 0.4)
    } else {
      if (this.queue.length >= 2) return // drop chatter, keep the feed readable
      this.queue.push({ text, tone })
    }
  }

  destroy(): void {
    for (const u of this.unsubs) u()
    this.unsubs = []
  }
}
